import { useGetHomeDashboard } from '@smarthome/api';
import { useSettingsStore } from '@smarthome/store';
import { ROUTES } from '@smarthome/ui';
import { LayoutDashboard, Plus, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui';
import { DashboardViewer } from '@/features/dashboards/viewer';

/**
 * Home Page - Shows the user's home dashboard or an empty state
 */
export function HomePage() {
  const { data, isLoading, isError, refetch } = useGetHomeDashboard();
  const { sidebarHidden } = useSettingsStore();
  const containerClassName = sidebarHidden ? '' : 'space-y-6';

  const dashboard = data?.dashboard;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={containerClassName}>
        {!sidebarHidden && (
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Home
            </h1>
          </div>
        )}
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load home dashboard</p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // No home dashboard set - show empty state with CTA
  if (!dashboard) {
    return (
      <div className={containerClassName}>
        {!sidebarHidden && (
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Home
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Your personal smart home dashboard
            </p>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white p-8 dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/20">
              <LayoutDashboard className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              No Home Dashboard Set
            </h2>
            <p className="mt-2 max-w-md text-center text-sm text-gray-500 dark:text-zinc-500">
              Create a custom dashboard to see your favorite devices, sensors, and controls at a
              glance. Set any dashboard as your home to see it here.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to={ROUTES.DASHBOARDS}>
                <Button variant="outline">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  View Dashboards
                </Button>
              </Link>
              <Link to={ROUTES.DASHBOARDS}>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Home dashboard is set - show the dashboard
  // When sidebar is hidden, hide the title for a true fullscreen experience
  return (
    <div className={containerClassName}>
      {!sidebarHidden && (
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            {dashboard.name}
          </h1>
          {dashboard.description && (
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              {dashboard.description}
            </p>
          )}
        </div>
      )}

      {/* Dashboard content */}
      <DashboardViewer document={dashboard.document} />
    </div>
  );
}
